import {Text} from '@gravity-ui/uikit';
import {FundraiserImagesModel, ImageType} from '../../model';
import styles from './FundraiserImages.module.scss';
import {ImageViewer} from '../ImageViewer';
import {ImagesList} from '../ImagesList';
import {ImageUploadButton} from '../ImageUploadButton';
import {useAppealsImages, useDragAndDrop, useImageViewer} from '../../lib';
import {useParams} from 'react-router';
import {useRef} from 'react';

interface FundraiserImagesProps {
  title: string;
  imageType: ImageType;
  fundraiserId?: string;
  images: FundraiserImagesModel;
  aspectRatio?: number;
}

export const FundraiserImages = ({title, aspectRatio, imageType, images}: FundraiserImagesProps) => {
  const {id} = useParams<{id: string}>();
  const uploadButtonRef = useRef<any>(null);

  const {
    getAppealsImages,
    deleteMainImageMutation,
    deleteFeedImageMutation,
    deleteAvatarImageMutation,
    updateMainImagesOrderMutation,
    updateFeedImagesOrderMutation,
  } = useAppealsImages({fundraiserId: id || ''});

  // Получение изображений по типу
  const getImagesByType = () => {
    switch (imageType) {
      case ImageType.MAIN:
        return images.mainImages;
      case ImageType.FEED:
        return images.feedImages;
      case ImageType.AVATAR:
        return images.avatar ? [images.avatar] : [];
      default:
        return [];
    }
  };

  const currentImages = getImagesByType();

  // Хуки для управления состоянием
  const imageViewer = useImageViewer();

  const dragAndDrop = useDragAndDrop({
    imageType,
    images: currentImages,
    fundraiserId: id || '',
    updateMainImagesOrder: (images) => {
      updateMainImagesOrderMutation.mutate(images);
    },
    updateFeedImagesOrder: (images) => {
      updateFeedImagesOrderMutation.mutate(images);
    },
  });

  // Обработчики
  const handleUploadSuccess = () => {
    getAppealsImages.refetch();
  };

  const handleUploadNewImage = () => {
    if (uploadButtonRef.current) {
      // Находим кнопку внутри компонента ImageUploadButton и программно кликаем по ней
      const button = uploadButtonRef.current.querySelector('button');
      if (button) {
        button.click();
      }
    }
  };

  const handleDeleteImage = async (imageType: ImageType, fileName: string) => {
    try {
      switch (imageType) {
        case ImageType.MAIN:
          await deleteMainImageMutation.mutateAsync(fileName);
          break;
        case ImageType.FEED:
          await deleteFeedImageMutation.mutateAsync(fileName);
          break;
        case ImageType.AVATAR:
          await deleteAvatarImageMutation.mutateAsync(fileName);
          break;
      }
    } catch (error) {
      console.error('Ошибка при удалении изображения:', error);
    }
  };

  return (
    <div className={styles.container}>
      <Text variant="subheader-1" className={styles.title}>
        {title}
      </Text>

      <ImagesList
        aspectRatio={aspectRatio}
        images={dragAndDrop.currentImages}
        imageType={imageType}
        fundraiserId={id}
        isDragDisabled={dragAndDrop.isDragDisabled}
        sensors={dragAndDrop.sensors}
        collisionDetection={dragAndDrop.collisionDetection}
        onDragEnd={dragAndDrop.handleDragEnd}
        onImageView={imageViewer.openImageViewer}
        onImageDelete={handleDeleteImage}
        onUploadSuccess={handleUploadSuccess}
        onUploadNewImage={handleUploadNewImage}
      />

      {/* Скрытая кнопка загрузки для программного клика */}
      <div ref={uploadButtonRef} style={{position: 'absolute', top: '-9999px', left: '-9999px'}}>
        <ImageUploadButton
          aspectRatio={aspectRatio}
          imageType={imageType}
          fundraiserId={id}
          onUploadSuccess={handleUploadSuccess}
        />
      </div>

      <ImageViewer
        open={imageViewer.isViewerOpen}
        onClose={imageViewer.closeImageViewer}
        images={imageViewer.viewerImages}
        currentImageIndex={imageViewer.currentImageIndex}
        onImageChange={imageViewer.setCurrentImageIndex}
      />
    </div>
  );
};
